import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiUserPlus,
  FiPlus,
  FiSearch,
  FiGrid,
  FiList,
  FiRotateCcw,
  FiInbox,
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification, { showToast } from "../../components/common/notification/Notification";
import SaaSLoadingSkeleton from "../../components/common/saas/SaaSLoadingSkeleton";
import usePersistentState from "../../hooks/usePersistentState";
import { getErrorText } from "../partnerNetwork/services/partnerNetworkService";
import { useRecordList } from "../partnerNetwork/hooks/useNetworkRecords";
import PersonAvatar from "../partnerNetwork/components/PersonAvatar";
import DeleteConfirmDialog from "../partnerNetwork/components/DeleteConfirmDialog";
import { useTeamService } from "./hooks/useTeamRecords";
import { TEAMS_HOME, TEAM_PAGE_SIZE } from "./constants/teamEndpoints";
import "../property/PropertiesPage.css";
import "../property/components/PropertyPageHeader.css";
import "../property/components/PropertyFilterToolbar.css";
import "../partnerNetwork/partnerNetwork.css";

const CREATE_PATH = `${TEAMS_HOME}/new`;

export const TeamStatusBadge = ({ status }) =>
  status ? <span className={`pn-badge ${String(status).toUpperCase() === "ACTIVE" ? "pn-badge--success" : "pn-badge--danger"}`}>{status}</span> : null;

// The avatar helper reads first_name, so a team name is passed as one
const teamAvatar = (team) => ({ first_name: team.name });

/** /partner/team - the teams owned by the logged-in partner (same endpoint as the mobile Team screen) */
const TeamsPage = () => {
  const navigate = useNavigate();
  const service = useTeamService();
  const load = useCallback(() => service.listTeams(), [service]);
  const { items: teams, loading, error, refetch } = useRecordList(load, "Unable to load teams. Please check your network connection.");

  const [currentPage, setCurrentPage] = usePersistentState("list:team:page", 1);
  const [searchTerm, setSearchTerm] = usePersistentState("list:team:search", "");
  const [viewMode, setViewMode] = usePersistentState("list:team:view", "grid");

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) => [t.name, t.description].some((v) => v && String(v).toLowerCase().includes(q)));
  }, [teams, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / TEAM_PAGE_SIZE));
  const paged = filtered.slice((currentPage - 1) * TEAM_PAGE_SIZE, currentPage * TEAM_PAGE_SIZE);
  const hasFilters = Boolean(searchTerm.trim());

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, setCurrentPage]);

  const resetFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const closeDialog = useCallback(() => setToDelete(null), []);

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await service.removeTeam(toDelete.id);
      showToast("Team deleted successfully", { type: "success" });
      setToDelete(null);
      refetch();
    } catch (err) {
      showToast(getErrorText(err, "Unable to delete team"), { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const renderActions = (team) => (
    <div className="pn-actions">
      <Link to={`${TEAMS_HOME}/${team.id}`} state={{ record: team }} className="btn btn-sm btn-outline-secondary">
        View
      </Link>
      <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" aria-label={`Edit ${team.name}`} onClick={() => navigate(`${TEAMS_HOME}/${team.id}/edit`, { state: { record: team } })}>
        <FiEdit2 size={14} />
      </button>
      <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" aria-label={`Delete ${team.name}`} onClick={() => setToDelete(team)}>
        <FiTrash2 size={14} />
      </button>
    </div>
  );

  const renderMeta = (team) => (
    <div className="pn-details">
      <span>
        <TeamStatusBadge status={team.status} />
        {team.total_seats != null && <span>{team.total_seats} seats</span>}
      </span>
    </div>
  );

  return (
    <div className="properties-module-page">
      <Notification />

      <div className="property-page-header">
        <div className="property-page-header__content">
          <div className="property-page-header__badge">
            <FiUserPlus className="property-page-header__badge-icon" />
            <span>Partner Team</span>
            {!loading && !error && <span className="property-page-header__count-tag">{teams.length} Listed</span>}
          </div>
          <h1 className="property-page-header__title">Teams</h1>
          <p className="property-page-header__description">Teams you have created and the members in them. Only teams created by your account appear here.</p>
        </div>
        <div className="property-page-header__actions">
          <Link to={CREATE_PATH} className="property-page-header__create-btn">
            <FiPlus size={18} />
            <span>Create Team</span>
          </Link>
        </div>
      </div>

      <div className="property-filter-toolbar">
        <div className="property-filter-toolbar__controls">
          <form className="property-filter-toolbar__search" role="search" onSubmit={(e) => e.preventDefault()}>
            <FiSearch className="property-filter-toolbar__search-icon" />
            <input
              type="text"
              aria-label="Search teams"
              placeholder="Search team name or description..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="property-filter-toolbar__search-input"
            />
            {searchTerm && (
              <button type="button" className="property-filter-toolbar__clear-btn" aria-label="Clear search" onClick={() => setSearchTerm("")}>
                ×
              </button>
            )}
          </form>

          <div className="property-filter-toolbar__selects">
            {hasFilters && (
              <button type="button" className="property-filter-reset-btn" onClick={resetFilters} title="Reset Filters">
                <FiRotateCcw size={14} />
                <span>Reset</span>
              </button>
            )}
          </div>

          <div className="property-view-toggle">
            <button type="button" aria-label="Grid view" className={`property-view-toggle__btn ${viewMode === "grid" ? "property-view-toggle__btn--active" : ""}`} onClick={() => setViewMode("grid")} title="Grid View">
              <FiGrid size={16} />
            </button>
            <button type="button" aria-label="List view" className={`property-view-toggle__btn ${viewMode === "table" ? "property-view-toggle__btn--active" : ""}`} onClick={() => setViewMode("table")} title="List View">
              <FiList size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div aria-busy="true" aria-label="Loading teams">
          <SaaSLoadingSkeleton type={viewMode === "grid" ? "card" : "list"} count={6} />
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
            Retry
          </button>
        </div>
      ) : paged.length === 0 ? (
        <div className="properties-empty-state">
          <div className="properties-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="properties-empty-state__title">{hasFilters ? "No Matching Teams" : "You haven't created any teams yet"}</h3>
          <p className="properties-empty-state__text">{hasFilters ? "Try adjusting your search terms." : "Create your first team to start adding members."}</p>
          {hasFilters ? (
            <button type="button" className="btn btn-outline-success mt-2" onClick={resetFilters}>
              Clear Search
            </button>
          ) : (
            <Link to={CREATE_PATH} className="btn btn-primary mt-2">
              Create Team
            </Link>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="properties-grid">
          {paged.map((team) => (
            <div key={team.id} className="pn-card">
              <div className="pn-card__head">
                <PersonAvatar person={teamAvatar(team)} />
                <h5 className="pn-card__name" title={team.name}>
                  <Link to={`${TEAMS_HOME}/${team.id}`} state={{ record: team }}>
                    {team.name}
                  </Link>
                </h5>
              </div>
              <p className="pn-card__desc">{team.description || "No description"}</p>
              {renderMeta(team)}
              {renderActions(team)}
            </div>
          ))}
        </div>
      ) : (
        <ul className="pn-rows">
          {paged.map((team) => (
            <li key={team.id} className="pn-row">
              <PersonAvatar person={teamAvatar(team)} />
              <div className="pn-row__main">
                <Link to={`${TEAMS_HOME}/${team.id}`} state={{ record: team }} className="pn-row__name">
                  {team.name}
                </Link>
                <span className="text-muted small">{team.description || "No description"}</span>
                {renderMeta(team)}
              </div>
              {renderActions(team)}
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && !loading && !error && (
        <div className="properties-pagination-bar">
          <button type="button" className="properties-page-nav-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>
          <span className="properties-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button type="button" className="properties-page-nav-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {toDelete && (
        <DeleteConfirmDialog
          title="Delete team?"
          message={`"${toDelete.name}" will be removed from your teams.`}
          busy={deleting}
          onCancel={closeDialog}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default TeamsPage;
